import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight, Rocket, Briefcase, Users, GraduationCap, LineChart,
  Sparkles, CheckCircle2, ArrowRight, Heart, Building2, Compass,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";

const AUDIENCE_KEYS = [
  { key: "talents",       color: "var(--mod-talent)",         icon: Users },
  { key: "entrepreneurs", color: "var(--mod-entrepreneur)",   icon: Rocket },
  { key: "entreprises",   color: "var(--mod-emploi)",         icon: Building2 },
  { key: "investisseurs", color: "var(--mod-investissement)", icon: LineChart },
  { key: "universites",   color: "var(--mod-formation)",      icon: GraduationCap },
];

const JOURNEY_KEYS = [
  { key: "idea",       icon: Sparkles },
  { key: "vision",     icon: Heart },
  { key: "project",    icon: Rocket },
  { key: "search",     icon: Compass },
  { key: "team",       icon: Users },
  { key: "collab",     icon: Briefcase },
  { key: "delivery",   icon: CheckCircle2 },
  { key: "employment", icon: Building2 },
  { key: "growth",     icon: LineChart },
];

const STORY_KEYS = [
  { key: "fatou", color: "var(--mod-talent)",       photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=3&w=400&h=400&q=80" },
  { key: "marc",  color: "var(--mod-emploi)",       photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=facearea&facepad=3&w=400&h=400&q=80" },
  { key: "amina", color: "var(--mod-entrepreneur)", photo: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&facepad=3&w=400&h=400&q=80" },
];

const TRUST_KEYS = ["passport", "coaching", "employment", "freelance", "entrepreneur", "invest"];

export default function Solutions() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const personas = [
    { initials: "AB", labelKey: "student",      photo: "https://images.unsplash.com/photo-1508243529287-e21914733111?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-formation), var(--afr-accent))",       x: "8%",  y: "18%" },
    { initials: "KD", labelKey: "developer",    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-freelance), var(--afr-accent))",      x: "56%", y: "8%"  },
    { initials: "MO", labelKey: "engineer",     photo: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-talent), var(--afr-accent))",         x: "78%", y: "42%" },
    { initials: "FN", labelKey: "entrepreneur", photo: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-entrepreneur), var(--afr-accent))",   x: "12%", y: "58%" },
    { initials: "SN", labelKey: "employer",     photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-emploi), var(--afr-accent))",         x: "62%", y: "68%" },
    { initials: "IV", labelKey: "investor",     photo: "https://images.unsplash.com/photo-1580894732930-0babd100d356?auto=format&fit=facearea&facepad=3&w=280&h=280&q=80", gradient: "linear-gradient(135deg, var(--mod-investissement), var(--afr-primary))", x: "40%", y: "86%" },
  ];

  return (
    <div className="afr-root" data-testid="afr-solutions-root">
      <SeoHead
        title="Solutions"
        description="Découvrez les solutions AFRICTAL : emploi certifié, missions freelance, formations panafricaines, incubation entrepreneurs, accompagnement investisseurs — un cadre unifié et de confiance."
        path="/solutions"
        keywords="solutions AFRICTAL, emploi, freelance Afrique, formation, entrepreneurs, investisseurs"
      />      <PublicNav />

      <div style={{ padding: "1.2rem 2rem 0" }}>
        <nav className="afr-breadcrumb" aria-label="breadcrumb" data-testid="solutions-breadcrumb">
          <Link to="/" data-testid="solutions-crumb-home">{t("common.breadcrumb_home")}</Link>
          <span className="afr-breadcrumb__sep">/</span>
          <span className="afr-breadcrumb__current">{t("solutions.breadcrumb")}</span>
        </nav>
      </div>

      {/* Rappel discret : les solutions s'appuient sur les écosystèmes */}
      <div
        style={{ padding: "0.4rem 2rem 0" }}
        data-testid="solutions-context-banner"
      >
        <div
          className="afr-flex-row"
          style={{
            gap: "0.8rem",
            padding: "0.8rem 1.1rem",
            background: "color-mix(in oklab, var(--afr-primary) 5%, var(--afr-surface))",
            border: "1px solid color-mix(in oklab, var(--afr-primary) 18%, var(--afr-border))",
            borderRadius: 14,
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="afr-flex-row" style={{ gap: "0.6rem", flex: "1 1 auto", minWidth: 0 }}>
            <Compass size={18} style={{ color: "var(--afr-primary)", flexShrink: 0 }} />
            <span className="afr-muted" style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
              {t("solutions.context_banner.prefix", "Les solutions ci-dessous s'appuient sur les")}{" "}
              <strong style={{ color: "var(--afr-secondary)" }}>{t("solutions.context_banner.strong", "8 écosystèmes AFRICTAL")}</strong>{" "}
              {t("solutions.context_banner.suffix", "— chaque parcours utilisateur combine emploi, formation, freelancing, entrepreneuriat, investissement…")}
            </span>
          </div>
          <Link
            to="/ecosystemes"
            className="afr-btn afr-btn--ghost afr-btn--sm"
            data-testid="solutions-back-to-ecosystems"
            style={{ flexShrink: 0, fontSize: "0.86rem", color: "var(--afr-primary)" }}
          >
            <ArrowUpRight size={14} style={{ transform: "rotate(-90deg)" }} /> {t("solutions.back_to_ecosystems", "Revoir les écosystèmes")}
          </Link>
        </div>
      </div>

      {/* ================================================
          SECTION 1 — HERO
      ================================================ */}
      <header className="afr-hero afr-hero--split" data-testid="solutions-hero">
        <div className="afr-hero__grid">
          <div className="afr-hero__left">
            <div className="afr-hero__meta">
              <span className="afr-eyebrow">{t("solutions.hero.eyebrow")}</span>
            </div>
            <h1 className="afr-hero__title">
              {t("solutions.hero.title_1")}<br />
              {t("solutions.hero.title_2")}<br />
              <em>{t("solutions.hero.title_3")}</em>
            </h1>
            <p className="afr-hero__lead">
              {t("solutions.hero.lead_1")}
              <br /><br />
              {t("solutions.hero.lead_2")}
            </p>

            <div className="afr-hero__cta-row" style={{ marginTop: "1.8rem", display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
              <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="solutions-cta-signup">
                <Rocket size={16} /> {t("solutions.hero.cta_signup")}
              </Link>
              <a href="#solutions" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="solutions-cta-discover">
                {t("solutions.hero.cta_discover")} <ArrowRight size={14} />
              </a>
            </div>

            <div className="afr-flex-row" style={{ marginTop: "1.4rem", gap: "0.8rem", flexWrap: "wrap" }} data-testid="solutions-trust-bar">
              {TRUST_KEYS.map((k) => (
                <span key={k} className="afr-badge" style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem", background: "var(--afr-bg-muted)" }}>
                  <CheckCircle2 size={12} style={{ verticalAlign: "middle", color: "var(--afr-primary)", marginRight: 4 }} />{t(`solutions.hero.trust.${k}`)}
                </span>
              ))}
            </div>
          </div>

          <div className="afr-hero__right" aria-hidden="true">
            <div className="afr-hero-scene" style={{ minHeight: 380 }}>
              <svg viewBox="0 0 480 380" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <radialGradient id="solGlow" cx="50%" cy="45%" r="60%">
                    <stop offset="0%" stopColor="rgba(0,150,64,0.12)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>
                <ellipse cx="240" cy="180" rx="200" ry="150" fill="url(#solGlow)" />
                {[
                  [80, 100, 240, 160], [400, 90, 240, 160], [90, 260, 240, 160],
                  [400, 270, 240, 160], [240, 320, 240, 160], [240, 60, 240, 160],
                ].map(([x1, y1, x2, y2], i) => (
                  <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(0,150,64,0.25)" strokeWidth="1.2" strokeDasharray="4 6" />
                ))}
                <circle cx="240" cy="180" r="22" fill="var(--afr-primary)" opacity="0.15" />
                <circle cx="240" cy="180" r="12" fill="var(--afr-primary)" />
                <text x="240" y="184" textAnchor="middle" fill="#fff" fontSize="10" fontFamily="Outfit" fontWeight="700">AFR</text>
              </svg>

              <div className="afr-hero-scene__personas">
                {personas.map((p) => {
                  const label = t(`solutions.hero.personas.${p.labelKey}`);
                  return (
                    <div key={p.labelKey} className="afr-hero-persona" style={{ left: p.x, top: p.y }} data-testid={`solutions-persona-${p.labelKey}`}>
                      <div className="afr-hero-persona__avatar afr-hero-persona__avatar--photo"
                           style={{ background: p.gradient, backgroundImage: `url(${p.photo}), ${p.gradient}`, backgroundSize: "cover", backgroundPosition: "center" }}>
                        <span className="afr-hero-persona__initials" aria-hidden="true">{p.initials}</span>
                      </div>
                      <span className="afr-hero-persona__label">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ================================================
          SECTION 2 — RÉALITÉ QUI CONCERNE TOUT LE MONDE
      ================================================ */}
      <section className="afr-section" data-testid="solutions-reality">
        <h2 className="afr-section__title">{t("solutions.reality.title")}</h2>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "2rem", marginTop: "1.5rem" }}>
          <article className="afr-card" style={{ borderLeft: "4px solid var(--mod-talent)" }} data-testid="reality-diplome">
            <div className="afr-card__badge" style={{ color: "var(--mod-talent)" }}>{t("solutions.reality.grad_badge")}</div>
            <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>
              {t("solutions.reality.grad_p1")}
            </p>
            <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>
              <strong>{t("solutions.reality.grad_p2")}</strong>
            </p>
          </article>

          <article className="afr-card" style={{ borderLeft: "4px solid var(--mod-emploi)" }} data-testid="reality-entreprise">
            <div className="afr-card__badge" style={{ color: "var(--mod-emploi)" }}>{t("solutions.reality.biz_badge")}</div>
            <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>
              {t("solutions.reality.biz_p1")}
            </p>
            <p style={{ fontSize: "1.02rem", lineHeight: 1.7, marginTop: "0.6rem" }}>
              <strong>{t("solutions.reality.biz_p2")}</strong>
            </p>
          </article>
        </div>
      </section>

      {/* ================================================
          SECTION 3 — SOLUTIONS PAR ACTEUR
      ================================================ */}
      <section id="solutions" className="afr-section" data-testid="solutions-audiences">
        <h2 className="afr-section__title">{t("solutions.audiences.title")}</h2>
        <p className="afr-section__lead">{t("solutions.audiences.lead")}</p>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.4rem", marginTop: "1.5rem" }}>
          {AUDIENCE_KEYS.map((s) => {
            const items = t(`solutions.audiences.cards.${s.key}.items`, { returnObjects: true });
            const itemList = Array.isArray(items) ? items : [];
            return (
              <article key={s.key} className="afr-card" data-testid={`solution-card-${s.key}`}
                       style={{ display: "flex", flexDirection: "column", borderTop: `4px solid ${s.color}` }}>
                <div className="afr-persona__icon" style={{ background: `linear-gradient(135deg, ${s.color}, var(--afr-accent))`, marginBottom: "0.6rem" }}>
                  <s.icon size={22} />
                </div>
                <div className="afr-card__badge" style={{ color: s.color }}>{t(`solutions.audiences.cards.${s.key}.audience`)}</div>
                <h3 style={{ fontFamily: "'Outfit',sans-serif", marginTop: "0.4rem", fontSize: "1.2rem", lineHeight: 1.3 }}>
                  {t(`solutions.audiences.cards.${s.key}.title`)}
                </h3>
                <ul className="afr-list" style={{ margin: "0.8rem 0", flex: 1 }}>
                  {itemList.map((it) => (
                    <li key={it}><CheckCircle2 size={13} style={{ verticalAlign: "middle", color: s.color, marginRight: 4 }} />{it}</li>
                  ))}
                </ul>
                <Link to="/login?mode=signup" className="afr-btn afr-btn--primary" data-testid={`solution-cta-${s.key}`} style={{ marginTop: "auto" }}>
                  {t("solutions.audiences.cta")} <ArrowUpRight size={14} />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* Note : la section Écosystèmes vit désormais sur sa page dédiée /ecosystemes
          pour éviter le doublon de navigation. */}


      {/* ================================================
          SECTION 4 — POURQUOI DIFFÉRENT + JOURNEY
      ================================================ */}
      <section className="afr-section" data-testid="solutions-journey">
        <h2 className="afr-section__title">{t("solutions.journey.title")}</h2>
        <p className="afr-section__lead" style={{ maxWidth: 780 }}>
          {t("solutions.journey.lead_p1")} <strong>{t("solutions.journey.lead_bold")}</strong> {t("solutions.journey.lead_p2")}
        </p>

        <ol className="afr-journey" data-testid="solutions-journey-timeline"
            style={{ listStyle: "none", padding: 0, margin: "2rem 0 0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.8rem" }}>
          {JOURNEY_KEYS.map((j, i) => (
            <li key={j.key} data-testid={`journey-step-${i}`}
                className="afr-card"
                style={{
                  padding: "1rem 0.8rem", textAlign: "center", position: "relative",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,150,64,0.18)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12, margin: "0 auto 0.5rem",
                background: `linear-gradient(135deg, var(--afr-primary), var(--afr-accent))`,
                display: "grid", placeItems: "center", color: "#fff",
              }}>
                <j.icon size={18} />
              </div>
              <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: "0.88rem", fontWeight: 600, color: "var(--afr-secondary)" }}>
                {i + 1}. {t(`solutions.journey.steps.${j.key}`)}
              </div>
              {i < JOURNEY_KEYS.length - 1 && (
                <span aria-hidden="true" style={{
                  position: "absolute", right: -14, top: "50%", transform: "translateY(-50%)",
                  color: "var(--afr-primary)", opacity: 0.35, fontSize: "1.4rem",
                }}>→</span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* ================================================
          SECTION 5 — HISTOIRES QUI INSPIRENT
      ================================================ */}
      <section className="afr-section" data-testid="solutions-stories">
        <h2 className="afr-section__title">{t("solutions.stories.title")}</h2>
        <p className="afr-section__lead">{t("solutions.stories.lead")}</p>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.4rem", marginTop: "1.5rem" }}>
          {STORY_KEYS.map((s) => (
            <article key={s.key} className="afr-card" data-testid={`solutions-story-${s.key}`}
                     style={{ overflow: "hidden", padding: 0 }}>
              <div style={{
                height: 180, backgroundImage: `url(${s.photo})`, backgroundSize: "cover", backgroundPosition: "center",
                position: "relative",
              }}>
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, transparent 40%, rgba(15,42,66,0.85))` }} />
                <div style={{ position: "absolute", bottom: 12, left: 16, color: "#fff" }}>
                  <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.3rem", fontWeight: 700 }}>{t(`solutions.stories.items.${s.key}.name`)}</div>
                  <div style={{ fontSize: "0.82rem", opacity: 0.9 }}>{t(`solutions.stories.items.${s.key}.role`)}</div>
                </div>
              </div>
              <div style={{ padding: "1.1rem 1.2rem" }}>
                <p style={{ fontSize: "0.94rem", lineHeight: 1.6, color: "var(--afr-secondary)" }}>« {t(`solutions.stories.items.${s.key}.text`)} »</p>
                <div className="afr-badge" style={{ marginTop: "0.6rem", background: s.color, color: "#fff", fontSize: "0.72rem" }}>{t("solutions.stories.badge")}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ================================================
          SECTION 6 — CTA FINAL
      ================================================ */}
      <section className="afr-section" data-testid="solutions-cta-final">
        <div className="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>{t("solutions.cta_final.title_1")}<br />{t("solutions.cta_final.title_2")}</h2>
            <p>{t("solutions.cta_final.lead")}</p>
          </div>
          <div className="afr-cta-banner__actions">
            <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="solutions-final-signup">
              <Rocket size={16} /> {t("solutions.cta_final.signup")}
            </Link>
            <Link to="/" className="afr-btn afr-btn--outline afr-btn--lg"
                  style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }} data-testid="solutions-final-discover">
              {t("solutions.cta_final.discover")} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
